import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import { Image as ImageIcon, Upload, Trash2, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const PostersManager = () => {
    const [posters, setPosters] = useState([]);
    const [title, setTitle] = useState('');
    const [link, setLink] = useState('');
    const [order, setOrder] = useState(0);
    const [file, setFile] = useState(null);
    const [saving, setSaving] = useState(false);
    const fileRef = useRef(null);
    const load = useCallback(async () => {
        try { const r = await axios.get(`${API_URL}/api/posters/all`, { withCredentials: true }); setPosters(r.data || []); } catch { /* silent */ }
    }, []);
    useEffect(() => { load(); }, [load]);
    const upload = async (e) => {
        e.preventDefault();
        if (!file) { toast.error('Choose an image first'); return; }
        setSaving(true);
        const fd = new FormData(); fd.append('file', file); fd.append('title', title); fd.append('link', link); fd.append('order', String(order || 0));
        try { await axios.post(`${API_URL}/api/posters`, fd, { withCredentials: true }); toast.success('Poster added'); setTitle(''); setLink(''); setFile(null); if (fileRef.current) fileRef.current.value = ''; load(); }
        catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
        finally { setSaving(false); }
    };
    const toggle = async (p, active) => {
        const fd = new FormData(); fd.append('active', active ? 'true' : 'false');
        try { await axios.put(`${API_URL}/api/posters/${p.id}`, fd, { withCredentials: true }); load(); } catch { toast.error('Failed'); }
    };
    const remove = async (p) => {
        if (!window.confirm('Delete this poster?')) return;
        try { await axios.delete(`${API_URL}/api/posters/${p.id}`, { withCredentials: true }); toast.success('Deleted'); load(); } catch { toast.error('Failed'); }
    };
    return (
        <Card className="card-surface" data-testid="posters-card">
            <CardHeader><CardTitle className="font-['Outfit'] flex items-center gap-2"><ImageIcon className="h-5 w-5 text-[#064E3B]" />Guardian dashboard posters</CardTitle><p className="text-sm text-[#4B5563]">School ads / announcements shown as a slideshow on every guardian's dashboard.</p></CardHeader>
            <CardContent className="space-y-4">
                <form onSubmit={upload} className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                    <div className="space-y-1"><Label>Title (optional)</Label><Input value={title} onChange={e => setTitle(e.target.value)} data-testid="poster-title" /></div>
                    <div className="space-y-1"><Label>Link (optional)</Label><Input value={link} onChange={e => setLink(e.target.value)} placeholder="https://facebook.com/..." data-testid="poster-link" /></div>
                    <div className="space-y-1"><Label>Order</Label><Input type="number" value={order} onChange={e => setOrder(Number(e.target.value))} data-testid="poster-order" /></div>
                    <div className="space-y-1"><Label>Image (max 4 MB)</Label><input ref={fileRef} type="file" accept="image/*" onChange={e => setFile(e.target.files?.[0] || null)} className="text-sm" data-testid="poster-file" /></div>
                    <Button type="submit" disabled={saving} className="bg-[#064E3B] hover:bg-[#047857] md:col-span-4 w-fit" data-testid="poster-upload-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}Add poster</Button>
                </form>
                {posters.length === 0 ? <p className="text-sm text-[#6B7280]">No posters yet.</p> : (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {posters.map(p => (
                            <div key={p.id} className="border rounded-lg overflow-hidden bg-white" data-testid={`poster-${p.id}`}>
                                <img src={`${API_URL}/api/posters/${p.id}/image`} alt={p.title || 'poster'} className="w-full h-32 object-cover" />
                                <div className="p-2 space-y-1 text-xs">
                                    <p className="font-medium truncate">{p.title || <span className="text-[#9CA3AF]">Untitled</span>} <Badge variant="outline" className="ml-1">#{p.order}</Badge></p>
                                    <div className="flex items-center justify-between"><label className="flex items-center gap-1"><Switch checked={p.active} onCheckedChange={(v) => toggle(p, v)} data-testid={`poster-active-${p.id}`} />{p.active ? 'Visible' : 'Hidden'}</label>
                                        <Button size="sm" variant="ghost" className="text-[#DC2626] h-7" onClick={() => remove(p)} data-testid={`poster-delete-${p.id}`}><Trash2 className="h-3.5 w-3.5" /></Button></div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

export const PostersCarousel = () => {
    const [posters, setPosters] = useState([]);
    const [idx, setIdx] = useState(0);
    useEffect(() => { axios.get(`${API_URL}/api/posters`, { withCredentials: true }).then(r => setPosters(r.data || [])).catch(() => {}); }, []);
    useEffect(() => {
        if (posters.length < 2) return undefined;
        const t = setInterval(() => setIdx(i => (i + 1) % posters.length), 6000);
        return () => clearInterval(t);
    }, [posters.length]);
    if (posters.length === 0) return null;
    const p = posters[idx];
    const img = <img src={`${API_URL}/api/posters/${p.id}/image`} alt={p.title || 'School announcement'} className="w-full max-h-[380px] object-contain bg-[#FAF9F6] transition-opacity duration-500" />;
    return (
        <Card className="card-surface overflow-hidden" data-testid="posters-carousel">
            {p.link ? <a href={p.link} target="_blank" rel="noopener noreferrer" data-testid="poster-slide-link">{img}</a> : img}
            {(p.title || posters.length > 1) && (
                <div className="flex items-center justify-between px-4 py-2">
                    <p className="text-sm font-medium text-[#1F2937]">{p.title}</p>
                    {posters.length > 1 && <div className="flex gap-1.5">{posters.map((x, i) => <button key={x.id} onClick={() => setIdx(i)} className={`h-2 w-2 rounded-full ${i === idx ? 'bg-[#064E3B]' : 'bg-[#D1D5DB]'}`} aria-label={`Slide ${i + 1}`} />)}</div>}
                </div>
            )}
        </Card>
    );
};
